import { Component, Input, Output, EventEmitter } from '@angular/core';
import { ForwardCurveData, ForwardCurveItem } from './forward-curve.model';
import { ComponentVisibilityService } from '@approot/services/component-view/component-visibility.service';
import {
  FxRate,
  MarketsService
} from '@approot/services/markets/markets.service';
import { Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-forward-curve',
  templateUrl: './forward-curve.component.html',
  styleUrls: ['./forward-curve.component.scss']
})
export class ForwardCurveComponent {
  @Input() currencyPairs: string[];
  @Output() currencyPairChanged = new EventEmitter<string>();
  @Input() widgetId;

  data: ForwardCurveData;

  periods = ['1W', '2W', '1M', '2M', '3M', '6M', '9M', '1Y'];

  periodToLabel = {
    ON: 'ON',
    '1W': '1w',
    '2W': '2w',
    '3W': '3w',
    '1M': '1m',
    '2M': '2m',
    '3M': '3m',
    '6M': '6m',
    '9M': '9m',
    '1Y': '12m'
  };

  maxValue = 0;
  minValue = 0;
  marketDataErrorMsg: string;
  dataLoaded: Observable<boolean>;
  positiveValuesClassNames = ['green1', 'green2', 'green3'];
  negativeValuesClassNames = ['red1', 'red2', 'red3'];

  constructor(
    private componentVisibilityService: ComponentVisibilityService,
    private marketsService: MarketsService
  ) {}

  valueClassName(value: number): string {
    if (!value) return '';
    const step = value > 0 ? this.maxValue / 3 : this.minValue / 3;
    const index = Math.ceil(value / step) - 1;
    if (index <= 2) {
      // console.log('Math.ceil: ' + Math.ceil(value / step));
      // console.log('step: ' + step);
      // console.log('index: ' + index);
      return value > 0
        ? this.positiveValuesClassNames[index]
        : this.negativeValuesClassNames[index];
    } else {
      return '';
    }
  }

  setMinMaxValues(data: ForwardCurveData) {
    const itemValues = Object.values(data).map((item: ForwardCurveItem) => [
      item.bid,
      item.ask
    ]);

    const values = [].concat
      .apply([], itemValues)
      .sort((a: number, b: number) => a - b);
    this.maxValue = values[values.length - 1];
    this.minValue = values[0];
  }

  pairToLabel(pair: string) {
    return pair.replace(/(\w{3})(\w{3})/g, '$1/$2');
  }

  onCurrencyPairChanged(value: string) {
    this.marketDataErrorMsg = '';
    this.marketsService.getLiveMarket([value]).then(p => {
      p.pipe(take(1)).subscribe(resp => {
        try {
          this.data = this.forwardCurveOperation(resp.fxRates[value]);
          this.setMinMaxValues(this.data);
          this.dataLoaded = of(true);
        } catch (error) {
          this.dataLoaded = of(false);
          this.data = {};
          this.marketDataErrorMsg = 'No market data found.';
        }
      });
    });
  }

  closeWidget() {
    this.componentVisibilityService.removeWidget('fwd', this.widgetId);
  }

  private forwardCurveOperation(d: FxRate): ForwardCurveData {
    const forwardCurve = {};
    Object.keys(d.forwardPoints).forEach(key => {
      // first time through it should set spotBid and spotAsk
      const field = d.forwardPoints[key];
      forwardCurve[key] = {
        bid: (field.mid - field.spread / 2) * d.forwardPointsMultiplier,
        ask: (field.mid + field.spread / 2) * d.forwardPointsMultiplier
      };
    });
    return forwardCurve;
  }
}
